import { debugLog, debugDir } from "../../../logger";
import {
  SimulationNode,
  SimulationLink,
  DynamicsParameters,
} from "../SimulationType";
import * as d3 from "d3";
import { EventEmitter } from "./EventEmitter";
//@ts-ignore
import forceBoundary from "d3-force-boundary";
import { CustomSet } from "./utils";

type SimulationEngineEventName = "seEnd" | "seTick";

export class SimulationEngine extends EventEmitter<SimulationEngineEventName> {
  svg: SVGSVGElement;
  private _nodeSize?: number;
  private repulseFactor = 8;
  private contactDetectorSensitivity = 1.5; // extends contact detection beyond nodeRadius
  private h: number;
  private w: number;
  padding = 10;
  private simulation: d3.Simulation<SimulationNode, SimulationLink>;
  centeringForce = false;
  private _zoom = 1.0;
  private simulationLinkStore = new CustomSet<SimulationLink>((l) => {
    // Key on the stable _frozen_id_, never the mutable `id` (renumbered on every
    // node removal). Keying on `id` would freeze a now-stale key into the
    // backing Map at add() time, so later has()/delete() could never match it.
    // Mirrors the link bookkeeping in SimulationViewer.
    const { source, target } = l;
    const min_id = Math.min(source._frozen_id_, target._frozen_id_);
    const max_id = Math.max(source._frozen_id_, target._frozen_id_);
    return `${min_id}::${max_id}`;
  });

  constructor(svg: SVGSVGElement) {
    super();
    this.svg = svg;
    this.h = svg.height.animVal.value;
    this.w = svg.width.animVal.value;

    this.simulation = d3.forceSimulation<SimulationNode, SimulationLink>();
  }
  // We want to restrict this call as much as possible
  // it is kept here for SuctomStach interaction
  // until we can rationalize it
  //
  getSimulationLayer() {
    return this.simulation;
  }

  private applyForces(sticky?: SimulationNode[]) {
    this.simulation.force(
      "charge",
      d3
        .forceManyBody()
        .strength(
          -this.nodeSize * this.repulseFactor * this._zoom * this._zoom,
        ),
    );

    /*
    debugLog(
      `SimulationEngine::force:collide radius set to ${this.nodeSize + 2}`,
    );
    this.simulation.force(
      "collide",
      d3.forceCollide().radius((d) => this.nodeSize / 2),
    );
    */

    const linksArrayForce = this.simulationLinkStore.list();
    debugLog("SimulationEngine::force:link input array is");
    debugLog(linksArrayForce);
    this.simulation.force(
      "link",
      d3
        .forceLink(linksArrayForce)
        .distance((this.nodeSize / 2) * (this._zoom * this._zoom))
        .strength(0.9),
    );

    this.simulation.force(
      "boundary",
      forceBoundary(
        this.padding,
        this.padding,
        this.w - this.padding,
        this.h - this.padding,
      ),
    );

    if (this.centeringForce) {
      debugLog("applying center force");
      this.simulation
        .force("xCenter", d3.forceX(this.w / 2).strength(0.2))
        .force("yCenter", d3.forceY(this.h / 2).strength(0.2));
    }

    if (sticky) {
    }
  }
  // We may have to enforce stop on simulation if width*height / layout change
  set width(w: number) {
    debugLog(`simulationEngine.width.setter ${w}`);
    this.w = w;
    this.applyForces();
    this.run();
  }
  // We may have to enforce stop on simulation if width*height / layout change
  set height(h: number) {
    debugLog(`simulationEngine.height.setter ${h}`);
    this.h = h;
    this.applyForces();
    this.run();
  }

  get nodeSize() {
    if (!this._nodeSize) return this.h / 8;
    return this._nodeSize;
  }

  set nodeSize(nodeSize: number) {
    this._nodeSize = nodeSize;
    this.applyForces();
  }

  set chargeFactor(value: number) {
    this.repulseFactor = value;
    this.applyForces();
  }

  restart() {
    this.applyForces();
    this.simulation.restart();
  }
  get nodes(): SimulationNode[] {
    return this.simulation.nodes();
  }
  set nodes(n: SimulationNode[]) {
    //appending nodes to current simulation.nodes
    this.simulation.nodes([...this.simulation.nodes(), ...n]);
    debugLog("SimulationEngine:nodes::setter:");
    debugDir(this.simulation.nodes());
  }

  links(v: SimulationLink[]) {
    v.forEach((s) => {
      this.simulationLinkStore.add(s);
    });
    // applyForces ?
  }
  link(source: SimulationNode, target: SimulationNode) {
    this.simulationLinkStore.add({ source, target });
    // applyForces ?
  }
  removeNodes(ns: SimulationNode[]) {
    // Match on the stable _frozen_id_, not `id`: ids are renumbered by
    // SimulationViewer.removeNodes (which runs first), so the survivors' ids
    // have already shifted by the time we get here.
    const toDelId = new Set<number>();
    ns.forEach((n) => toDelId.add(n._frozen_id_));
    const new_nodes = this.simulation
      .nodes()
      .filter((n) => !toDelId.has(n._frozen_id_));
    this.simulation.nodes(new_nodes);
    // Purge links incident to any removed node so forceLink never references a
    // node that is no longer in the simulation.
    this.simulationLinkStore
      .list()
      .filter(
        (l) =>
          toDelId.has(l.source._frozen_id_) ||
          toDelId.has(l.target._frozen_id_),
      )
      .forEach((l) => this.simulationLinkStore.delete(l));
    // applyForces ??
  }
  removeLinks(links: [SimulationNode, SimulationNode][]) {
    links.forEach((l) => {
      const [source, target] = l;
      if (!this.simulationLinkStore.has({ source, target })) {
        debugLog(`SimulationEngine:: no such link (as node pair) found`);
        debugDir({ source, target });
      }
      // source/target assignment doesnt matter as linkStore accessor
      // is sorting source, target id keys
      this.simulationLinkStore.delete({ source, target });
    });
  }

  /*
           Resolve overlaps for a pinned node `c` (e.g. the node being dragged) against
        a set of obstacles given as `candidates` selections (e.g. the anchored
         nodes). Internally relies on nodeContactDetector to find the contact, then
         projects `c` out to the contact rim, updating its position/pin in place.
          Repeats since pushing out of one obstacle may move `c` into another.
        Returns the last obstacle hit (undefined when no correction was needed).
        */
  nodesFrameRepulsor(
    movingNodes: SimulationNode[],
    candidates?: SimulationNode[],
  ): (SimulationNode | undefined)[] {
    debugLog("nodeFrameRepulsor::");
    debugDir(movingNodes);
    debugDir(candidates);

    return movingNodes.map((c) => {
      // Probe from the pinned target so detection happens at the intended
      // position rather than the previous tick's coordinates.
      if (c.fx != null) c.x = c.fx;
      if (c.fy != null) c.y = c.fy;

      const minDist = (this.nodeSize / 2) * this._zoom;
      let hit: SimulationNode | undefined;
      let contact = this.nodeContactDetector(c, candidates);
      let guard = 0;
      while (contact && guard++ < 4) {
        hit = contact;
        const dx = (c.x ?? 0) - (contact.x ?? 0);
        const dy = (c.y ?? 0) - (contact.y ?? 0);
        const dist = Math.hypot(dx, dy) || 1e-6;
        c.x = (contact.x ?? 0) + (dx / dist) * minDist;
        c.y = (contact.y ?? 0) + (dy / dist) * minDist;
        c.fx = c.x;
        c.fy = c.y;
        contact = this.nodeContactDetector(c, candidates);
      }
      return hit;
    });
  }

  nodeContactDetector(
    c: SimulationNode,
    candidates?: SimulationNode[],
  ): SimulationNode | undefined {
    /*
      Returns the closes SimulationNode which surface intersects the provided SimulationNode.
      When `candidates` is provided (e.g. the anchored selection), only the nodes bound to
      those selections are tested instead of the full simulation node set.
    */

    const nodeList: SimulationNode[] = candidates ? candidates : this.nodes;

    let closest: [number, SimulationNode | undefined] = [
      (this.nodeSize / 2) * this.contactDetectorSensitivity * this._zoom,
      undefined,
    ];
    debugLog(
      `==>Contact checker (d:${this.nodeSize / 2})  * (z:${this._zoom}) over ${nodeList.length} nodes`,
    );

    nodeList.forEach((d: SimulationNode) => {
      debugDir(c);
      debugLog(d.id, c.id);
      if (d.id === c.id) return;
      const dist = Math.sqrt(
        ((c.x ?? 0) - (d.x ?? 0)) * ((c.x ?? 0) - (d.x ?? 0)) +
          ((c.y ?? 0) - (d.y ?? 0)) * ((c.y ?? 0) - (d.y ?? 0)),
      );
      debugLog(`${dist} vs ${closest[0]}`);
      if (dist < closest[0]) closest = [dist, d];
    });
    debugLog(`======>Contact checker returns`);
    debugLog(closest[1]);
    return closest[1];
  }

  // We may have to enforce stop on simulation if width*height / layout change
  run({
    alpha = 0.1,
    alphaMin = 0.005,
    velocityDecay = 0.15,
    stickyNodes = [],
  }: DynamicsParameters = {}) {
    debugLog("SimulationEngine:run");
    debugDir(this.simulation.nodes());

    this.applyForces(stickyNodes); // just in case :p
    const nodes = this.simulation.nodes();
    if (nodes.length === 0) {
      debugLog("SimulationEngine::run abort start, empty nodes array");
      return;
    }
    stickyNodes.forEach((d) => {
      d.fx = d.x;
      d.fy = d.y;
    });
    const self = this;
    this.simulation
      .on("tick", () => {
        self.emit("seTick");
      })
      .alpha(alpha)
      .alphaMin(alphaMin)
      .velocityDecay(velocityDecay)
      .on("end", () => {
        debugLog("SimulationEngine:run::END");
        self.emit("seEnd");
        nodes.forEach((d) => {
          if (d.manually_anchored) return;
          d.fx = null;
          d.fy = null;
        });
      })
      .restart();
  }

  zoom(value: number, simulate = true) {
    this._zoom = value;
    this.applyForces();
    if (simulate) {
      this.run();
    }
  }
}
